import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import SaveProductButton from '../components/SaveProductButton';

import { apiGet } from '../config/apiClient';
import slugify from '../utils/slugify';

export default function StoreDetailPage() {
  const { storeIdentifier } = useParams(); // Get slug or ID from URL
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsPagination, setProductsPagination] = useState(null);
  const [productsLoading, setProductsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentProductPage = parseInt(searchParams.get('prod_page') || '1', 10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!storeIdentifier) return;
    const fetchStoreDetails = async (productPage = 1) => {
      setLoading(true);
      setProductsLoading(true);
      setError('');
      try {
        const data = await apiGet(`/stores/${storeIdentifier}?prod_page=${productPage}`);
        setStore(data);
        if (data.products_paginated) {
          setProducts(data.products_paginated.data || []);
          setProductsPagination({
            current_page: data.products_paginated.current_page,
            last_page: data.products_paginated.last_page,
            from: data.products_paginated.from,
            to: data.products_paginated.to,
            total: data.products_paginated.total,
            next_page_url: data.products_paginated.next_page_url,
            prev_page_url: data.products_paginated.prev_page_url,
          });
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Fetch store error:', err);
        setError(err.data?.message || err.message || 'An error occurred while fetching store details.');
      }
      setLoading(false);
      setProductsLoading(false);
    };
    fetchStoreDetails(currentProductPage);
  }, [storeIdentifier, currentProductPage]);

  const handleProductSaveStatusChange = (productId, newSavedStatus) => {
    setProducts(prevProducts => 
      prevProducts.map(product => 
        product.id === productId ? { ...product, is_saved_by_user: newSavedStatus } : product
      )
    );
  };

  const handleProductPageChange = (newPage) => {
    if (newPage >= 1 && productsPagination && newPage <= productsPagination.last_page) {
      setSearchParams({ prod_page: newPage.toString() });
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading store details...</div>;
  }

  if (error) {
    return <div className="text-center py-20 text-red-500 bg-red-100 p-4 rounded">Error: {error}</div>;
  }

  if (!store) {
    return <div className="text-center py-20">Store not found.</div>;
  }

  // Assumes store object has fields like name, logo_url, address, city, description
  const logoUrl = store.logo_url || '/placeholder-product.svg';

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-10">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
          <img 
            src={logoUrl} 
            alt={`${store.name} logo`} 
            className="w-48 h-auto object-contain rounded-lg border border-gray-200 p-2 bg-white"
            style={{maxHeight: '120px'}}
          />
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2">{store.name || 'Store Name N/A'}</h1>
            {store.address && <p className="text-gray-600 mb-1"><span className="font-semibold">Main Address:</span> {store.address}</p>}
            {store.city && <p className="text-gray-600 mb-1"><span className="font-semibold">City:</span> {store.city}</p>}
          </div>
        </div>
        {store.branches && store.branches.length > 0 && (
          <div className="mb-6 border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-3">
              Branch Locations ({store.branches.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {store.branches.map((branch) => (
                <div key={branch.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-red-800 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      {branch.name && <p className="font-semibold text-gray-800">{branch.name}</p>}
                      {branch.address && <p className="text-sm text-gray-600">{branch.address}</p>}
                      {branch.neighborhood && <p className="text-sm text-gray-500">{branch.neighborhood}</p>}
                      {branch.phone && <p className="text-sm text-gray-500">{branch.phone}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {store.description && (
          <div className="mb-6 border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-700 mb-2">About {store.name}</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{store.description}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mt-10">
        <h2 className="text-2xl font-bold text-gray-700 mb-6">Products Available at {store?.name || 'this store'}</h2>
        {productsLoading && <p className="text-gray-500">Loading products...</p>}
        {!productsLoading && products.length === 0 && <p className="text-gray-500">No products listed for this store currently.</p>}
        {!productsLoading && products.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <div key={product.id} className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col hover:shadow-lg transition-shadow duration-200 ease-in-out">
                  <Link 
                    to={`/product/${product.id}`} 
                    className="flex flex-col items-center p-4 flex-grow"
                  >
                    <img 
                      src={product.image_url || '/placeholder-product.svg'} 
                      alt={product.name} 
                      className="w-32 h-32 object-cover rounded-lg mb-3 shadow-sm"
                    />
                    <h3 className="font-semibold text-gray-800 text-center text-sm mb-1 h-10 overflow-hidden">{product.name}</h3>
                    {product.price && <div className="text-red-800 font-bold text-md">${parseFloat(product.price).toFixed(2)}</div>}
                  </Link>
                  <div className="p-3 pt-0 border-t border-gray-100">
                    <Link 
                        to={`/product/${product.id}`} 
                        className="block w-full text-center bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition text-xs mb-1.5"
                    >
                        View Details
                    </Link>
                    <SaveProductButton 
                        productId={product.id} 
                        initialIsSaved={product.is_saved_by_user || false} 
                        onSaveStatusChange={handleProductSaveStatusChange} 
                    />
                  </div>
                </div>
              ))}
            </div>
            {productsPagination && productsPagination.last_page > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{productsPagination.from || 0}</span> to <span className="font-semibold">{productsPagination.to || 0}</span> of <span className="font-semibold">{productsPagination.total || 0}</span> products
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleProductPageChange(1)} disabled={productsPagination.current_page === 1} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">First</button>
                  <button onClick={() => handleProductPageChange(productsPagination.current_page - 1)} disabled={!productsPagination.prev_page_url} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">&laquo; Prev</button>
                  <span className="text-sm text-gray-700">
                    Page <input type="number" value={productsPagination.current_page} onChange={(e) => handleProductPageChange(parseInt(e.target.value,10))} min="1" max={productsPagination.last_page} className="mx-1 px-2 py-1 w-12 text-center border rounded-md"/> of {productsPagination.last_page}
                  </span>
                  <button onClick={() => handleProductPageChange(productsPagination.current_page + 1)} disabled={!productsPagination.next_page_url} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next &raquo;</button>
                  <button onClick={() => handleProductPageChange(productsPagination.last_page)} disabled={productsPagination.current_page === productsPagination.last_page} className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Last</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
} 